/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomStyledDatePicker from '../../../Components/Datepicker/CustomStyledDatePicker';
import {
  fromPickerDate,
  ModalShell,
  toISODate,
  toPickerDate,
} from './modalsShared';
export { ProgressEditModal } from './projectDetailsModals';


const DEFAULT_RISK_FORM_DATA = {
  description: '',
  impactId: null,
  impact: '',
  riskStatusId: null,
  riskStatus: '',
  categoryId: null,
  assetCategory: '',
  owner: '',
  likelihoodId: null,
  likelihood: '',
  targetDate: '',
  revisedTargetDate: '',
  mitigationStrategyId: null,
  mitigation: '',
  measures: '',
};

const normalizeIdLabelOptions = (options = []) => (
  (Array.isArray(options) ? options : [])
    .filter((option) => option?.value !== null && option?.value !== undefined && option?.label)
    .map((option) => ({ ...option, value: Number(option.value), label: String(option.label) }))
    .filter((option) => Number.isFinite(option.value))
);
const normalizeValueLabelOptions = (options = []) => (
  (Array.isArray(options) ? options : [])
    .filter((option) => option?.value !== null && option?.value !== undefined && option?.label)
    .map((option) => ({ value: option.value, label: String(option.label) }))
);
const buildCommonSubmitMeta = (data) => ({
  id: data?.id,
  row_id: data?.row_id ?? data?.id,
  _entity_type: data?._entity_type || 'change_request',
  category_id: data?.category_id ?? null,
  type_id: data?.type_id ?? null,
});
const handleSubmitWithCommonMeta = (event, onSubmit, formData, data) => {
  event.preventDefault();
  onSubmit({ ...formData, ...buildCommonSubmitMeta(data) });
};
const normalizeOptionalIdValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const findOptionByIdOrLabel = (options, idValue, labelValue) => {
  const normalizedOptions = normalizeIdLabelOptions(options);
  const numericId = Number(idValue);
  if (Number.isFinite(numericId)) {
    const byId = normalizedOptions.find((option) => option.value === numericId);
    if (byId) return byId;
  }
  const labelText = String(labelValue || '')
    .trim()
    .toLowerCase();
  if (!labelText) return null;
  return normalizedOptions.find((option) => option.label.trim().toLowerCase() === labelText) || null;
};

const getSelectIdValue = (value) => (
  value !== null && value !== undefined ? String(value) : ''
);
const ID_LABEL_OPTION_PROP_TYPE = PropTypes.arrayOf(PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
}));

const ModalActions = ({
  onClose,
  isSubmitting = false,
  submitLabel = 'Update',
  submittingLabel = 'Updating...',
}) => (
  <div className="modal-actions">
    <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
      Cancel
    </button>
    <button type="submit" className="btn-primary" disabled={isSubmitting}>
      {isSubmitting ? submittingLabel : submitLabel}
    </button>
  </div>
);

const IdLabelSelectField = ({
  id,
  label,
  value,
  idField,
  labelField,
  objectField,
  options,
  placeholder,
  fallbackLabel = '',
  setFormData,
}) => (
  <div className="form-group">
    <label htmlFor={id}>{label}</label>
    <select
      id={id}
      value={getSelectIdValue(value)}
      onChange={(event) => setFormData(
        (previousState) => updateIdAndLabel(previousState, {
          idField,
          labelField,
          objectField,
          nextId: event.target.value ? Number(event.target.value) : null,
          options,
        }),
      )}
    >
      <option value="" disabled>{placeholder}</option>
      {!options.some((option) => String(option.value) === getSelectIdValue(value))
        && getSelectIdValue(value)
        && fallbackLabel ? (
          <option value={getSelectIdValue(value)}>
            {fallbackLabel}
          </option>
        ) : null}
      {options.map((option) => (
        <option key={option.value} value={String(option.value)}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);
const DatePickerField = ({
  label,
  value,
  onChange,
  wrapperClassName = 'form-group form-group-date',
}) => (
  <div className={wrapperClassName}>
    <div className="certification-date-container custom-project-details-date-container">
      <label className="datepicker-input-label">{label}</label>
      <CustomStyledDatePicker
        label=""
        placeholder="Choose date"
        value={toPickerDate(value)}
        onChange={onChange}
        displayFormat="DD-MM-YYYY"
      />
    </div>
  </div>
);

const updateIdAndLabel = (previousState, {
  idField,
  labelField,
  objectField,
  nextId,
  options,
}) => {
  const selectedOption = options.find((option) => option.value === nextId);
  return {
    ...previousState,
    [idField]: nextId,
    [labelField]: selectedOption?.label || '',
    ...(objectField
      ? {
        [objectField]: selectedOption
          ? { ...selectedOption, id: selectedOption.value, label: selectedOption.label }
          : null,
      }
      : {}),
  };
};
ModalActions.propTypes = {
  onClose: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
};

IdLabelSelectField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  idField: PropTypes.string.isRequired,
  labelField: PropTypes.string.isRequired,
  objectField: PropTypes.string,
  options: ID_LABEL_OPTION_PROP_TYPE.isRequired,
  placeholder: PropTypes.string.isRequired,
  fallbackLabel: PropTypes.string,
  setFormData: PropTypes.func.isRequired,
};

DatePickerField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  onChange: PropTypes.func.isRequired,
  wrapperClassName: PropTypes.string,
};

const buildRiskEditFormData = (
  data,
  impactOptions = [],
  riskStatusOptions = [],
  likelihoodOptions = [],
  mitigationOptions = [],
) => {
  const impactOption = findOptionByIdOrLabel(
    impactOptions,
    data?.impact_id ?? data?.impactId,
    data?.impact,
  );
  const likelihoodOption = findOptionByIdOrLabel(
    likelihoodOptions,
    data?.likelihood_id ?? data?.likelihoodId,
    data?.likelihood,
  );
  const mitigationOption = findOptionByIdOrLabel(
    mitigationOptions,
    data?.mitigation_strategy_id ?? data?.mitigationStrategyId,
    data?.mitigation,
  );
  const riskStatusOption = findOptionByIdOrLabel(
    riskStatusOptions,
    data?.risk_status_id ?? data?.riskStatusId ?? data?.status_id ?? data?.statusId,
    data?.riskStatus || data?.status,
  );

  return {
    ...DEFAULT_RISK_FORM_DATA,
    description: data?.description || '',
    impactId: impactOption?.value ?? null,
    impact: impactOption?.label || data?.impact || '',
    riskStatusId: riskStatusOption?.value ?? null,
    riskStatus: riskStatusOption?.label || data?.riskStatus || data?.status || '',
    categoryId: data?.category_id ?? data?.categoryId ?? null,
    assetCategory: data?.assetCategory || data?.category || '',
    owner: data?.owner || '',
    likelihoodId: likelihoodOption?.value ?? null,
    likelihood: likelihoodOption?.label || data?.likelihood || '',
    targetDate: resolveIssuePrefillDate(
      data?.target_date,
      data?.targetDate,
      data?.targeted_date,
      data?.targetedDate,
    ),
    revisedTargetDate: resolveIssuePrefillDate(
      data?.revised_target_date,
      data?.revisedTargetDate,
      data?.revised_targeted_date,
      data?.revisedTargetedDate,
      data?.revised_targeted_date,
    ),
    mitigationStrategyId: mitigationOption?.value ?? null,
    mitigation: mitigationOption?.label || data?.mitigation || '',
    measures: data?.measures || '',
  };
};

const resolveRiskCategoryMeta = (formData, categoryOptions) => {
  const resolvedCategoryOptions = (Array.isArray(categoryOptions) ? categoryOptions : [])
    .filter((option) => option?.value !== null && option?.value !== undefined && option?.label);
  const selectedCategoryValue = (
    formData.categoryId !== null && formData.categoryId !== undefined
      ? String(formData.categoryId)
      : formData.assetCategory
  );
  const hasSelectedLabelInOptions = resolvedCategoryOptions
    .some((option) => String(option.label) === String(formData.assetCategory));

  return { resolvedCategoryOptions, selectedCategoryValue, hasSelectedLabelInOptions };
};

const resolveIssuePrefillDate = (...values) => {
  const dateValue = values.find((value) => value !== null && value !== undefined && value !== '');
  return toISODate(dateValue || '');
};

const buildIssueEditFormData = ({
  data,
  statusOptions = [],
  categoryOptions = [],
  impactOptions = [],
}) => {
  const issueStatusOption = findOptionByIdOrLabel(
    statusOptions,
    data?.issue_status_id ?? data?.risk_status_id ?? data?.issueStatusId ?? data?.riskStatusId,
    data?.issueStatus ?? data?.riskStatus ?? data?.status,
  );
  const categoryOption = findOptionByIdOrLabel(
    categoryOptions,
    data?.issue_category_id ?? data?.category_id ?? data?.issueCategoryId ?? data?.categoryId,
    data?.category,
  );
  const impactOption = findOptionByIdOrLabel(
    impactOptions,
    data?.impact_id ?? data?.impactId,
    data?.impact,
  );
  return {
    description: data?.description || '',
    assignedTo: data?.assigned_to || data?.assignedTo || data?.owner || '',
    issueStatusId: issueStatusOption?.value ?? null,
    issueStatus: issueStatusOption?.label || data?.status || '',
    categoryId: categoryOption?.value ?? null,
    category: categoryOption?.label || data?.category || '',
    impactId: impactOption?.value ?? null,
    targetDate: resolveIssuePrefillDate(
      data?.target_date,
      data?.targeted_date,
      data?.targetDate,
      data?.targetedDate,
    ),
    revisedTargetDate: resolveIssuePrefillDate(
      data?.revised_target_date,
      data?.revised_date,
      data?.revisedDate,
      data?.revised_targeted_date,
      data?.revisedTargetedDate,
      data?.revisedTargetDate,
    ),
    statusUpdates: data?.status_update || data?.statusUpdates || data?.risk_treatment || '',
  };
};

const resolveInfoSecPrefillDate = (...values) => {
  const dateValue = values.find((value) => value !== null && value !== undefined && value !== '');
  return toISODate(dateValue || '');
};

const buildInfoSecEditFormData = ({
  data,
  categoryOptions = [],
  impactOptions = [],
  riskStatusOptions = [],
  likelihoodOptions = [],
  mitigationOptions = [],
}) => {
  const categoryOption = findOptionByIdOrLabel(
    categoryOptions,
    data?.asset_category_id ?? data?.category_id ?? data?.assetCategoryId ?? data?.categoryId,
    data?.assetCategory ?? data?.category,
  );
  const impactOption = findOptionByIdOrLabel(
    impactOptions,
    data?.impact_id ?? data?.impactId,
    data?.impact,
  );
  const likelihoodOption = findOptionByIdOrLabel(
    likelihoodOptions,
    data?.likelihood_id ?? data?.liklihood_id ?? data?.likelihoodId,
    data?.likelihood,
  );
  const riskStatusOption = findOptionByIdOrLabel(
    riskStatusOptions,
    data?.risk_status_id ?? data?.riskStatusId ?? data?.status_id ?? data?.statusId,
    data?.riskStatus ?? data?.status,
  );
  const mitigationOption = findOptionByIdOrLabel(
    mitigationOptions,
    data?.mitigation_strategy_id
      ?? data?.risk_mitigation_strategy_id
      ?? data?.mitigation_statergy_id
      ?? data?.mitigationStrategyId,
    data?.mitigation,
  );

  return {
    description: data?.infosec_description || data?.risk_description || data?.description || '',
    categoryId: categoryOption?.value ?? null,
    assetCategory: categoryOption?.label || data?.assetCategory || data?.category || '',
    likelihoodId: likelihoodOption?.value ?? null,
    likelihood: likelihoodOption?.label || data?.likelihood || '',
    impactId: impactOption?.value ?? null,
    impact: impactOption?.label || data?.impact || '',
    impactOption: impactOption ? { id: impactOption.value, label: impactOption.label } : null,
    riskStatusId: riskStatusOption?.value ?? null,
    riskStatus: riskStatusOption?.label || data?.riskStatus || data?.status || '',
    riskStatusOption: riskStatusOption ? { id: riskStatusOption.value, label: riskStatusOption.label } : null,
    targetDate: resolveInfoSecPrefillDate(
      data?.target_date,
      data?.targeted_date,
      data?.targetDate,
      data?.targetedDate,
    ),
    revisedTargetDate: resolveInfoSecPrefillDate(
      data?.revised_target_date,
      data?.revised_date,
      data?.revisedDate,
      data?.revised_targeted_date,
      data?.revisedTargetDate,
      data?.revisedTargetedDate,
    ),
    mitigationStrategyId: mitigationOption?.value ?? null,
    mitigation: mitigationOption?.label || data?.mitigation || '',
    mitigationOption: mitigationOption ? { id: mitigationOption.value, label: mitigationOption.label } : null,
    measures: data?.measures_taken || data?.risk_treatment || data?.status_update || data?.measures || '',
    owner: data?.owner || '',
    totalAssetValue: data?.total_asset_value || data?.totalAssetValue || '',
    assetCategoryOption: categoryOption ? { id: categoryOption.value, label: categoryOption.label } : null,
    likelihoodOption: likelihoodOption ? { id: likelihoodOption.value, label: likelihoodOption.label } : null,
  };
};

const DEFAULT_INFOSEC_FORM_DATA = {
  description: '',
  categoryId: null,
  assetCategory: '',
  assetCategoryOption: null,
  likelihoodId: null,
  likelihood: '',
  likelihoodOption: null,
  impactId: null,
  impact: '',
  impactOption: null,
  riskStatusId: null,
  riskStatus: '',
  riskStatusOption: null,
  targetDate: '',
  revisedTargetDate: '',
  mitigationStrategyId: null,
  mitigation: '',
  mitigationOption: null,
  measures: '',
  owner: '',
  totalAssetValue: '',
};

const resolveInfoSecSelectOptions = ({
  categoryOptions,
  impactOptions,
  riskStatusOptions,
  likelihoodOptions,
  mitigationOptions,
}) => ({
  resolvedCategoryOptions: normalizeIdLabelOptions(categoryOptions),
  resolvedImpactOptions: normalizeIdLabelOptions(impactOptions),
  resolvedRiskStatusOptions: normalizeIdLabelOptions(riskStatusOptions),
  resolvedLikelihoodOptions: normalizeIdLabelOptions(likelihoodOptions),
  resolvedMitigationOptions: normalizeIdLabelOptions(mitigationOptions),
});

export const DeleteConfirmationModal = ({
  isOpen,
  title,
  message,
  onClose,
  onConfirm,
  isSubmitting = false,
}) => {
  if (!isOpen) return null;

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} title={title} size="small">
      <div className="delete-confirmation">
        <span className="warning-icon" aria-hidden="true"></span>
        <div className="confirmation-text">{message}</div>
      </div>
      <div className="modal-actions">
        <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="button" className="btn-danger" onClick={onConfirm} disabled={isSubmitting}>
          {isSubmitting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </ModalShell>
  );
};

DeleteConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

export const CSATEditModal = ({
  isOpen,
  data,
  customers = [],
  isCustomersLoading = false,
  customersError = null,
  onClose,
  onHistory,
  onSubmit,
  isSubmitting = false,
}) => {
  const isEditMode = Boolean(data?.id || data?.row_id);
  const [formData, setFormData] = useState({
    date: '',
    customerId: '',
    serviceQuality: '',
    roi: '',
    management: '',
    overall: '',
    feedback: '',
  });

  useEffect(() => {
    if (!data) return;

    setFormData({
      date: toISODate(data.date || data.receivedDate || data.csat_rec_date),
      customerId: (
        data?.customerId !== null && data?.customerId !== undefined && data?.customerId !== ''
          ? String(data.customerId)
          : data?.customer_id !== null && data?.customer_id !== undefined && data?.customer_id !== ''
            ? String(data.customer_id)
            : ''
      ),
      serviceQuality: data.serviceQuality || data.quality_of_service || '',
      roi: data.roi || data.return_on_investment || '',
      management: data.management || data.management_communication || '',
      overall: data.overall || data.csat_rating || '',
      feedback: data.feedback || data.remarks || data.otherFeedback || data.other_feedback || '',
    });
  }, [data]);

  const handleSubmit = (event) => handleSubmitWithCommonMeta(event, onSubmit, formData, data);

  if (!isOpen) return null;
  const selectedCustomerName = data?.customer || data?.customer_name || '';
  const hasMappedSelectedCustomer = customers.some(
    (customer) => String(customer.customer_id) === String(formData.customerId),
  );
  let submitButtonLabel = '';
  if (isSubmitting) {
    submitButtonLabel = isEditMode ? 'Updating...' : 'Adding...';
  } else {
    submitButtonLabel = isEditMode ? 'Update' : 'Add';
  }

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit CSAT' : 'Add CSAT'}
      size="medium"
      modalClassName="edit-modal edit-csat-modal"
      headerAction={(
        <button
          type="button"
          className="modal-header-link"
          onClick={onHistory || (() => {})}
        >
          History
        </button>
      )}
    >
      <form className="modal-form csat-compact-form" onSubmit={handleSubmit}>
        <div className="form-group form-group-date">
          <div className="certification-date-container custom-project-details-date-container">
            <label className="datepicker-input-label">CSAT received date</label>
            <CustomStyledDatePicker
              label=""
              placeholder="Choose date"
              value={toPickerDate(formData.date)}
              onChange={(date) => setFormData({ ...formData, date: fromPickerDate(date) })}
              displayFormat="DD-MM-YYYY"
              maxDate={dayjs()}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="csat-customer-name">Customer name</label>
          <select
            id="csat-customer-name"
            value={formData.customerId}
            onChange={(event) => setFormData({
              ...formData,
              customerId: event.target.value,
            })}
            disabled={Boolean(customersError)}
            required
          >
            <option value="" disabled>{isCustomersLoading ? 'Loading customers...' : 'Select customer'}</option>
            {!hasMappedSelectedCustomer && formData.customerId && selectedCustomerName ? (
              <option value={formData.customerId}>{selectedCustomerName}</option>
            ) : null}
            {customers.map((customer) => (
              <option key={customer.customer_id} value={customer.customer_id}>
                {customer.customer_name}
              </option>
            ))}
          </select>
          {customersError ? <small className="field-error">Unable to load customers</small> : null}
        </div>

        <div className="form-group">
          <label htmlFor="csat-other-feedback">Other feedback</label>
          <textarea
            id="csat-other-feedback"
            value={formData.feedback}
            onChange={(event) => setFormData({ ...formData, feedback: event.target.value })}
            placeholder="Enter feedback"
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="csat-service-quality">How happy are you with the quality of service from Niveus?</label>
          <input
            id="csat-service-quality"
            className="csat-score-input"
            type="number"
            step="0.1"
            min="0"
            max="5"
            value={formData.serviceQuality}
            onChange={(event) => setFormData({ ...formData, serviceQuality: event.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="csat-roi-score">How happy are you with the Return-On-Investment on your project?</label>
          <input
            id="csat-roi-score"
            className="csat-score-input"
            type="number"
            step="0.1"
            min="0"
            max="5"
            value={formData.roi}
            onChange={(event) => setFormData({ ...formData, roi: event.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="csat-management-score">
            How happy are you with the project management and communication on your project?
          </label>
          <input
            id="csat-management-score"
            className="csat-score-input"
            type="number"
            step="0.1"
            min="0"
            max="5"
            value={formData.management}
            onChange={(event) => setFormData({ ...formData, management: event.target.value })}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="csat-overall-score">How would you like to rate your overall experience?</label>
          <input
            id="csat-overall-score"
            className="csat-score-input"
            type="number"
            step="0.1"
            min="0"
            max="5"
            value={formData.overall}
            onChange={(event) => setFormData({ ...formData, overall: event.target.value })}
            required
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {submitButtonLabel}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

CSATEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.object,
  customers: PropTypes.arrayOf(PropTypes.shape({
    customer_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    customer_name: PropTypes.string,
  })),
  isCustomersLoading: PropTypes.bool,
  customersError: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onHistory: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
};

export const ProjectUnitEditModal = ({
  isOpen,
  data,
  categoryOptions = [],
  onClose,
  onSubmit,
  isSubmitting = false,
  title = 'Add New Unit',
  submitLabel = 'Add',
  submittingLabel = 'Adding...',
}) => {
  const normalizedCategoryOptions = normalizeIdLabelOptions(categoryOptions);
  const selectedCategoryOption = findOptionByIdOrLabel(
    normalizedCategoryOptions,
    data?.resourceCategoryId ?? data?.resourceCategory ?? data?.resource_category,
    data?.resourceCategory,
  );
  const [formData, setFormData] = useState({
    resourceCategoryId: '',
    skills: '',
    quantity: '',
    unitPrice: '',
    startDate: '',
    endDate: '',
    remarks: '',
  });

  useEffect(() => {
    if (!data) return;
    setFormData({
      resourceCategoryId: selectedCategoryOption?.value ? String(selectedCategoryOption.value) : '',
      skills: data?.skills || '',
      quantity: data?.quantity || '',
      unitPrice: data?.unitPriceValue ?? data?.unitPrice ?? data?.unit_price ?? '',
      startDate: toISODate(data?.startDate || data?.start_date),
      endDate: toISODate(data?.endDate || data?.end_date),
      remarks: data?.remarks || '',
    });
  }, [data, selectedCategoryOption?.value]);

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="medium"
      modalClassName="edit-modal edit-project-unit-modal"
    >
      <form
        className="modal-form project-unit-compact-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({
            ...formData,
            id: data?.id ?? data?.unitId ?? data?.row_id,
          });
        }}
      >
        <div className="form-group">
          <label htmlFor="project-unit-resource-category">Resource category</label>
          <select
            id="project-unit-resource-category"
            value={formData.resourceCategoryId}
            onChange={(event) => setFormData({ ...formData, resourceCategoryId: event.target.value })}
            required
          >
            <option value="" disabled>Select resource category</option>
            {normalizedCategoryOptions.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="project-unit-skills">Resource skills</label>
          <input
            id="project-unit-skills"
            type="text"
            value={formData.skills}
            onChange={(event) => setFormData({ ...formData, skills: event.target.value })}
            placeholder="Enter resource skills"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="project-unit-quantity">Qty</label>
            <input
              id="project-unit-quantity"
              type="number"
              step="0.01"
              min="0"
              value={formData.quantity}
              onChange={(event) => setFormData({ ...formData, quantity: event.target.value })}
              placeholder="Enter quantity"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="project-unit-cost">Unit Cost/M</label>
            <input
              id="project-unit-cost"
              type="number"
              step="0.01"
              min="0"
              value={formData.unitPrice}
              onChange={(event) => setFormData({ ...formData, unitPrice: event.target.value })}
              placeholder="Enter unit cost"
              required
            />
          </div>
        </div>

        <div className="form-row">
          <DatePickerField
            label="Start date"
            value={formData.startDate}
            onChange={(date) => setFormData({ ...formData, startDate: fromPickerDate(date) })}
          />
          <DatePickerField
            label="End date"
            value={formData.endDate}
            onChange={(date) => setFormData({ ...formData, endDate: fromPickerDate(date) })}
          />
        </div>

        <div className="form-group">
          <label htmlFor="project-unit-remarks">Remarks</label>
          <textarea
            id="project-unit-remarks"
            value={formData.remarks}
            onChange={(event) => setFormData({ ...formData, remarks: event.target.value })}
            placeholder="Enter remarks"
          />
        </div>

        <ModalActions
          onClose={onClose}
          isSubmitting={isSubmitting}
          submitLabel={submitLabel}
          submittingLabel={submittingLabel}
        />
      </form>
    </ModalShell>
  );
};

ProjectUnitEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.object,
  categoryOptions: ID_LABEL_OPTION_PROP_TYPE,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
};

export const EffortEditModal = ({
  isOpen,
  data,
  categoryOptions = [],
  typeOptions = [],
  onClose,
  onSubmit,
  isSubmitting = false,
  title = 'Update effort',
  submitLabel = 'Update',
  submittingLabel = 'Updating...',
}) => {
  const isEditMode = Boolean(data?.row_id ?? data?.id);
  const resolvedTitle = isEditMode ? title : 'Add effort';
  const resolvedSubmitLabel = isEditMode ? submitLabel : 'Add';
  const resolvedSubmittingLabel = isEditMode ? submittingLabel : 'Adding...';
  const resolvedCategoryOptions = normalizeValueLabelOptions(categoryOptions);
  const resolvedTypeOptions = normalizeIdLabelOptions(typeOptions);
  const hasOthersOption = resolvedCategoryOptions.some(
    (option) => String(option.label || '')
      .trim()
      .toLowerCase() === 'others',
  );
  const categorySelectOptions = isEditMode
    ? resolvedCategoryOptions
    : hasOthersOption
      ? resolvedCategoryOptions
      : [...resolvedCategoryOptions, { value: 'others', label: 'Others' }];
  const [formData, setFormData] = useState({
    categoryId: '',
    customCategory: '',
    typeId: '',
    efforts: '',
    details: '',
  });

  useEffect(() => {
    if (!isOpen || !data) return;
    const matchedCategoryOption = categorySelectOptions.find(
      (option) => String(option.label || '')
        .trim()
        .toLowerCase()
        === String(data?.resource_category || '')
          .trim()
          .toLowerCase(),
    );

    setFormData({
      categoryId: matchedCategoryOption?.value ?? data.category_id ?? '',
      customCategory: '',
      typeId: data.type_id ?? '',
      efforts: data.efforts_hours_display ?? data.estimated_efforts ?? data.efforts_hour ?? data.efforts_hours ?? '',
      details: data.details || '',
    });
  }, [isOpen, data?.id, data?.row_id]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      ...formData,
      id: data?.id,
      row_id: data?.row_id ?? data?.id,
      _entity_type: data?._entity_type || 'effort_estimation',
      category_id: normalizeOptionalIdValue(formData?.categoryId),
      category_label: (
        categorySelectOptions.find((option) => String(option.value) === String(formData?.categoryId))?.label
        || data?.resource_category
        || ''
      ),
      custom_category: formData?.customCategory || '',
      type_id: normalizeOptionalIdValue(formData?.typeId),
    });
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={resolvedTitle}
      size="medium"
      modalClassName="edit-modal edit-effort-modal"
    >
      <form className="modal-form effort-wide-form" onSubmit={handleSubmit}>
        <div className="form-row effort-single-row">
          <div className="form-group">
            <label htmlFor="effort-resource-category">Resource category</label>
            <select
              id="effort-resource-category"
              value={String(formData.categoryId || '')}
              onChange={(event) => setFormData({ ...formData, categoryId: event.target.value })}
              required
            >
              <option value="" disabled>Select category</option>
              {categorySelectOptions.map((option) => (
                <option key={String(option.value)} value={String(option.value)}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="effort-resource-skill">Resource skills</label>
            <select
              id="effort-resource-skill"
              value={String(formData.typeId || '')}
              onChange={(event) => setFormData({ ...formData, typeId: event.target.value })}
              required
            >
              <option value="" disabled>Select skill</option>
              {resolvedTypeOptions.map((option) => (
                <option key={String(option.value)} value={String(option.value)}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="effort-hours">{isEditMode ? 'Estimated Hrs' : 'Efforts'}</label>
            <input
              id="effort-hours"
              type="number"
              step="0.01"
              min="0"
              value={formData.efforts}
              onChange={(event) => setFormData({ ...formData, efforts: event.target.value })}
              required
            />
          </div>
        </div>

        {!isEditMode && String(formData.categoryId || '').toLowerCase() === 'others' ? (
          <div className="form-row effort-single-row">
            <div className="form-group">
              <label htmlFor="effort-custom-category">Category</label>
              <input
                id="effort-custom-category"
                type="text"
                value={formData.customCategory}
                onChange={(event) => setFormData({ ...formData, customCategory: event.target.value })}
                placeholder="Enter category"
                required
              />
            </div>
          </div>
        ) : null}

        <div className="form-row effort-single-row">
          <div className="form-group">
            <label htmlFor="effort-details">Details</label>
            <input
              id="effort-details"
              type="text"
              value={formData.details}
              onChange={(event) => setFormData({ ...formData, details: event.target.value })}
              placeholder="Enter details..."
            />
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? resolvedSubmittingLabel : resolvedSubmitLabel}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

EffortEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.object,
  categoryOptions: ID_LABEL_OPTION_PROP_TYPE,
  typeOptions: ID_LABEL_OPTION_PROP_TYPE,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
};

export const AuditEditModal = ({
  isOpen,
  data,
  onClose,
  onSubmit,
  isSubmitting = false,
  title = 'Update audit score',
  submitLabel = 'Update',
  submittingLabel = 'Updating...',
}) => {
  const [formData, setFormData] = useState({
    date: '',
    processCompliance: '',
    quality: '',
    technology: '',
  });

  useEffect(() => {
    if (!data) return;

    setFormData({
      date: toISODate(data.processDate),
      processCompliance: data.processScore || '',
      quality: data.qualityScore || '',
      technology: data.technologyScore || '',
    });
  }, [data]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      ...formData,
      id: data?.id,
      row_id: data?.row_id ?? data?.id,
      processAuditNameId: data?.processAuditNameId ?? null,
      processScoreId: data?.processScoreId ?? null,
      qualityAuditNameId: data?.qualityAuditNameId ?? null,
      qualityScoreId: data?.qualityScoreId ?? null,
      technologyAuditNameId: data?.technologyAuditNameId ?? null,
      technologyScoreId: data?.technologyScoreId ?? null,
    });
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="medium"
      modalClassName="edit-modal"
    >
      <form className="modal-form audit-compact-form" onSubmit={handleSubmit}>
        <div className="form-group form-group-date">
          <div className="certification-date-container custom-project-details-date-container">
            <label className="datepicker-input-label">Date</label>
            <CustomStyledDatePicker
              label=""
              placeholder="Choose date"
              value={toPickerDate(formData.date)}
              onChange={(date) => setFormData({ ...formData, date: fromPickerDate(date) })}
              displayFormat="DD-MM-YYYY"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="audit-process-compliance">Process compliance</label>
          <input
            id="audit-process-compliance"
            type="text"
            value={formData.processCompliance}
            onChange={(event) => setFormData({ ...formData, processCompliance: event.target.value })}
            placeholder="Enter process compliance"
          />
        </div>

        <div className="form-group">
          <label htmlFor="audit-quality">Quality</label>
          <input
            id="audit-quality"
            className="audit-quality-input"
            type="text"
            value={formData.quality}
            onChange={(event) => setFormData({ ...formData, quality: event.target.value })}
            placeholder="Enter quality"
          />
        </div>

        <div className="form-group">
          <label htmlFor="audit-technology">Technology</label>
          <input
            id="audit-technology"
            type="text"
            value={formData.technology}
            onChange={(event) => setFormData({ ...formData, technology: event.target.value })}
            placeholder="Enter technology"
          />
        </div>

        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? submittingLabel : submitLabel}
          </button>
        </div>
      </form>
    </ModalShell>
  );
};

AuditEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
};

export const RiskEditModal = ({
  isOpen,
  data,
  categoryOptions = [],
  impactOptions = [],
  riskStatusOptions = [],
  likelihoodOptions = [],
  mitigationOptions = [],
  onClose,
  onSubmit,
  isSubmitting = false,
  title = 'Update Risk',
  submitLabel = 'Update',
  submittingLabel = 'Updating...',
}) => {
  const [formData, setFormData] = useState(DEFAULT_RISK_FORM_DATA);

  useEffect(() => {
    if (!data) return;
    setFormData(buildRiskEditFormData(
      data,
      impactOptions,
      riskStatusOptions,
      likelihoodOptions,
      mitigationOptions,
    ));
  }, [data, impactOptions, riskStatusOptions, likelihoodOptions, mitigationOptions]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formData, id: data?.id, row_id: data?.row_id ?? data?.id });
  };

  if (!isOpen) return null;

  const { resolvedCategoryOptions, selectedCategoryValue, hasSelectedLabelInOptions }
    = resolveRiskCategoryMeta(formData, categoryOptions);
  const resolvedImpactOptions = normalizeIdLabelOptions(impactOptions);
  const resolvedRiskStatusOptions = normalizeIdLabelOptions(riskStatusOptions);
  const resolvedLikelihoodOptions = normalizeIdLabelOptions(likelihoodOptions);
  const resolvedMitigationOptions = normalizeIdLabelOptions(mitigationOptions);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="large"
      modalClassName="edit-modal risk-edit-modal"
    >
      <form className="modal-form risk-compact-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="risk-description">Risk description</label>
          <textarea
            id="risk-description"
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
            placeholder="Enter description..."
            rows="3"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="risk-category">Risk category</label>
            <select
              id="risk-category"
              value={selectedCategoryValue}
              onChange={(event) => {
                const nextValue = event.target.value;
                const selectedOption = resolvedCategoryOptions
                  .find((option) => String(option.value) === String(nextValue));
                if (selectedOption) {
                  setFormData({
                    ...formData,
                    assetCategory: String(selectedOption.label),
                    categoryId: selectedOption.value,
                  });
                  return;
                }
                setFormData({
                  ...formData,
                  assetCategory: nextValue,
                  categoryId: null,
                });
              }}
            >
              <option value="" disabled>Select category</option>
              {resolvedCategoryOptions.map((option) => (
                <option key={String(option.value)} value={String(option.value)}>
                  {option.label}
                </option>
              ))}
              {!formData.categoryId && formData.assetCategory && !hasSelectedLabelInOptions ? (
                <option value={formData.assetCategory}>
                  {formData.assetCategory}
                </option>
              ) : null}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="risk-owner">Owner</label>
            <input
              id="risk-owner"
              type="text"
              value={formData.owner}
              onChange={(event) => setFormData({ ...formData, owner: event.target.value })}
              placeholder="Enter owner"
            />
          </div>
        </div>

        <div className="form-row">
          <IdLabelSelectField
            id="risk-likelihood"
            label="Likelihood"
            value={formData.likelihoodId}
            idField="likelihoodId"
            labelField="likelihood"
            options={resolvedLikelihoodOptions}
            placeholder="Select likelihood"
            setFormData={setFormData}
          />
          <IdLabelSelectField
            id="risk-impact"
            label="Impact"
            value={formData.impactId}
            idField="impactId"
            labelField="impact"
            options={resolvedImpactOptions}
            placeholder="Select impact"
            setFormData={setFormData}
          />
        </div>

        <div className="form-row">
          <IdLabelSelectField
            id="risk-status"
            label="Risk status"
            value={formData.riskStatusId}
            idField="riskStatusId"
            labelField="riskStatus"
            options={resolvedRiskStatusOptions}
            placeholder="Select status"
            setFormData={setFormData}
          />
          <div className="form-group form-group-date">
            <div className="certification-date-container custom-project-details-date-container">
              <label className="datepicker-input-label">Target date</label>
              <CustomStyledDatePicker
                label=""
                placeholder="Choose date"
                value={toPickerDate(formData.targetDate)}
                onChange={(date) => setFormData({ ...formData, targetDate: fromPickerDate(date) })}
                displayFormat="DD-MM-YYYY"
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group form-group-date">
            <div className="certification-date-container custom-project-details-date-container">
              <label className="datepicker-input-label">Revised Target Date</label>
              <CustomStyledDatePicker
                label=""
                placeholder="Choose date"
                value={toPickerDate(formData.revisedTargetDate)}
                onChange={(date) => setFormData({ ...formData, revisedTargetDate: fromPickerDate(date) })}
                displayFormat="DD-MM-YYYY"
              />
            </div>
          </div>
          <IdLabelSelectField
            id="risk-mitigation-strategy"
            label="Risk mitigation strategy"
            value={formData.mitigationStrategyId}
            idField="mitigationStrategyId"
            labelField="mitigation"
            options={resolvedMitigationOptions}
            placeholder="Select strategy"
            setFormData={setFormData}
          />
        </div>

        <div className="form-row single">
          <div className="form-group">
            <label htmlFor="risk-measures">Measures to address risk</label>
            <input
              id="risk-measures"
              type="text"
              value={formData.measures}
              onChange={(event) => setFormData({ ...formData, measures: event.target.value })}
              placeholder="Enter measures to address risk"
            />
          </div>
        </div>

        <ModalActions
          onClose={onClose}
          isSubmitting={isSubmitting}
          submitLabel={submitLabel}
          submittingLabel={submittingLabel}
        />
      </form>
    </ModalShell>
  );
};

RiskEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.object,
  categoryOptions: ID_LABEL_OPTION_PROP_TYPE,
  impactOptions: ID_LABEL_OPTION_PROP_TYPE,
  riskStatusOptions: ID_LABEL_OPTION_PROP_TYPE,
  likelihoodOptions: ID_LABEL_OPTION_PROP_TYPE,
  mitigationOptions: ID_LABEL_OPTION_PROP_TYPE,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
};

export const IssueEditModal = ({
  isOpen,
  data,
  statusOptions = [],
  categoryOptions = [],
  impactOptions = [],
  onClose,
  onSubmit,
  isSubmitting = false,
  title = 'Update Issue',
  submitLabel = 'Update',
  submittingLabel = 'Updating...',
}) => {
  const isEditMode = Boolean(data?.id || data?.row_id);
  const [formData, setFormData] = useState({
    description: '',
    assignedTo: '',
    issueStatusId: null,
    issueStatus: '',
    categoryId: null,
    category: '',
    impactId: null,
    targetDate: '',
    revisedTargetDate: '',
    statusUpdates: '',
  });

  useEffect(() => {
    if (!data) return;
    setFormData(buildIssueEditFormData({
      data,
      statusOptions,
      categoryOptions,
      impactOptions,
    }));
  }, [data, statusOptions, categoryOptions, impactOptions]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formData, id: data?.id, row_id: data?.row_id ?? data?.id });
  };

  if (!isOpen) return null;
  const resolvedIssueStatusOptions = normalizeIdLabelOptions(statusOptions);
  const resolvedIssueCategoryOptions = normalizeIdLabelOptions(categoryOptions);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="large"
      modalClassName="edit-modal issue-edit-modal"
    >
      <form className="modal-form issue-compact-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="issue-description">Issue description</label>
          <textarea
            id="issue-description"
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
            placeholder="Enter description..."
            rows="3"
          />
        </div>

        <div className="form-row">
          <IdLabelSelectField
            id="issue-category"
            label="Issue category"
            value={formData.categoryId}
            idField="categoryId"
            labelField="category"
            options={resolvedIssueCategoryOptions}
            placeholder="Select category"
            setFormData={setFormData}
          />
          <IdLabelSelectField
            id="issue-priority"
            label="Priority"
            value={formData.impactId}
            idField="impactId"
            labelField="impact"
            options={normalizeIdLabelOptions(impactOptions)}
            placeholder="Select priority"
            setFormData={setFormData}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="issue-assigned-to">Assigned to</label>
            <input
              id="issue-assigned-to"
              type="text"
              value={formData.assignedTo}
              onChange={(event) => setFormData({ ...formData, assignedTo: event.target.value })}
              placeholder="Enter assigned to"
            />
          </div>
          <IdLabelSelectField
            id="issue-status"
            label="Issue status"
            value={formData.issueStatusId}
            idField="issueStatusId"
            labelField="issueStatus"
            options={resolvedIssueStatusOptions}
            placeholder="Select status"
            setFormData={setFormData}
          />
        </div>

        <div className="form-row">
          <DatePickerField
            label="Target date"
            value={formData.targetDate}
            onChange={(date) => setFormData({ ...formData, targetDate: fromPickerDate(date) })}
          />
          <div className="form-group">
            <label htmlFor="issue-status-updates">Status updates</label>
            <input
              id="issue-status-updates"
              type="text"
              value={formData.statusUpdates}
              onChange={(event) => setFormData({ ...formData, statusUpdates: event.target.value })}
              placeholder="Enter status updates"
            />
          </div>
        </div>

        {isEditMode && (
          <div className="form-row" style={{ paddingBottom: '20px' }}>
            <DatePickerField
              label="Revised target date"
              value={formData.revisedTargetDate}
              onChange={(date) => setFormData({ ...formData, revisedTargetDate: fromPickerDate(date) })}
            />
            <div className="form-group" />
          </div>
        )}

        <ModalActions
          onClose={onClose}
          isSubmitting={isSubmitting}
          submitLabel={submitLabel}
          submittingLabel={submittingLabel}
        />
      </form>
    </ModalShell>
  );
};

IssueEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.object,
  statusOptions: ID_LABEL_OPTION_PROP_TYPE,
  categoryOptions: ID_LABEL_OPTION_PROP_TYPE,
  impactOptions: ID_LABEL_OPTION_PROP_TYPE,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
};

export const InfoSecEditModal = ({
  isOpen,
  data,
  categoryOptions = [],
  impactOptions = [],
  riskStatusOptions = [],
  likelihoodOptions = [],
  mitigationOptions = [],
  onClose,
  onSubmit,
  isSubmitting = false,
  title = 'Update Infosec Risk',
  submitLabel = 'Update',
  submittingLabel = 'Updating...',
}) => {
  const isEditMode = Boolean(data?.id || data?.row_id);
  const [formData, setFormData] = useState(DEFAULT_INFOSEC_FORM_DATA);
  useEffect(() => {
    if (!data) return;
    setFormData(buildInfoSecEditFormData({
      data,
      categoryOptions,
      impactOptions,
      riskStatusOptions,
      likelihoodOptions,
      mitigationOptions,
    }));
  }, [data, categoryOptions, impactOptions, riskStatusOptions, likelihoodOptions, mitigationOptions]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({ ...formData, id: data?.id, row_id: data?.row_id ?? data?.id });
  };

  if (!isOpen) return null;
  const {
    resolvedCategoryOptions,
    resolvedImpactOptions,
    resolvedRiskStatusOptions,
    resolvedLikelihoodOptions,
    resolvedMitigationOptions,
  } = resolveInfoSecSelectOptions({
    categoryOptions,
    impactOptions,
    riskStatusOptions,
    likelihoodOptions,
    mitigationOptions,
  });
  const handleAssetCategoryChange = (event) => {
    const nextId = event.target.value ? Number(event.target.value) : null;
    setFormData((previousState) => {
      const nextState = updateIdAndLabel(previousState, {
        idField: 'categoryId',
        labelField: 'assetCategory',
        objectField: 'assetCategoryOption',
        nextId,
        options: resolvedCategoryOptions,
      });
      const selectedOption = resolvedCategoryOptions.find((option) => option.value === nextId);
      const totalAssetValue = selectedOption?.total_asset_value ?? selectedOption?.totalAssetValue;
      return totalAssetValue === null || totalAssetValue === undefined
        ? nextState
        : { ...nextState, totalAssetValue: String(totalAssetValue) };
    });
  };

  const totalAssetValueTooltip = (
    <div className="total-asset-tooltip">
      Total Asset Value = [Confidentiality(C) + Integrity(I) + Availability(A)]&nbsp;*&nbsp;Weight of the Asset (AW)
    </div>
  );

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="large"
      modalClassName="edit-modal infosec-edit-modal"
    >
      <form className="modal-form infosec-compact-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="infosec-risk-description">Risk description</label>
          <textarea
            id="infosec-risk-description"
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
            placeholder="Enter description..."
            rows="3"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="infosec-asset-category">Asset Category-Element</label>
            <select
              id="infosec-asset-category"
              value={getSelectIdValue(formData.categoryId)}
              onChange={handleAssetCategoryChange}
            >
              <option value="" disabled>Select category</option>
              {!resolvedCategoryOptions.some((option) => String(option.value) === getSelectIdValue(formData.categoryId))
                && getSelectIdValue(formData.categoryId)
                && formData.assetCategory ? (
                  <option value={getSelectIdValue(formData.categoryId)}>{formData.assetCategory}</option>
                ) : null}
              {resolvedCategoryOptions.map((option) => (
                <option key={option.value} value={String(option.value)}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="infosec-total-asset-value">
              <span className="label-with-tooltip">
                Total asset value
                <Tooltip title={totalAssetValueTooltip} arrow placement="top">
                  <span
                    className="info-tooltip-icon"
                    role="button"
                    tabIndex={0}
                    aria-label="Total asset value info"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <InfoOutlinedIcon fontSize="inherit" />
                  </span>
                </Tooltip>
              </span>
            </label>
            <input
              id="infosec-total-asset-value"
              type="number"
              value={formData.totalAssetValue}
              onChange={(event) => setFormData({ ...formData, totalAssetValue: event.target.value })}
              placeholder="Enter total asset value"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="infosec-owner">Owner</label>
            <input
              id="infosec-owner"
              type="text"
              value={formData.owner}
              onChange={(event) => setFormData({ ...formData, owner: event.target.value })}
              placeholder="Enter owner"
            />
          </div>
          <IdLabelSelectField
            id="infosec-likelihood"
            label="Likelihood"
            value={formData.likelihoodId}
            idField="likelihoodId"
            labelField="likelihood"
            objectField="likelihoodOption"
            options={resolvedLikelihoodOptions}
            placeholder="Select likelihood"
            setFormData={setFormData}
          />
        </div>

        <div className="form-row">
          <IdLabelSelectField
            id="infosec-impact"
            label="Impact"
            value={formData.impactId}
            idField="impactId"
            labelField="impact"
            objectField="impactOption"
            options={resolvedImpactOptions}
            placeholder="Select impact"
            setFormData={setFormData}
          />
          <IdLabelSelectField
            id="infosec-risk-status"
            label="Risk status"
            value={formData.riskStatusId}
            idField="riskStatusId"
            labelField="riskStatus"
            objectField="riskStatusOption"
            options={resolvedRiskStatusOptions}
            placeholder="Select status"
            setFormData={setFormData}
          />
        </div>

        <div className="form-row">
          <DatePickerField
            label="Target date"
            value={formData.targetDate}
            onChange={(date) => setFormData({ ...formData, targetDate: fromPickerDate(date) })}
          />
          {isEditMode ? (
            <DatePickerField
              label="Revised target date"
              value={formData.revisedTargetDate}
              onChange={(date) => setFormData({ ...formData, revisedTargetDate: fromPickerDate(date) })}
            />
          ) : (
            <div className="form-group" aria-hidden="true"></div>
          )}
        </div>

        <div className="form-row">
          <IdLabelSelectField
            id="infosec-mitigation-strategy"
            label="Risk mitigation strategy"
            value={formData.mitigationStrategyId}
            idField="mitigationStrategyId"
            labelField="mitigation"
            objectField="mitigationOption"
            options={resolvedMitigationOptions}
            placeholder="Select strategy"
            setFormData={setFormData}
          />
          <div className="form-group">
            <label htmlFor="infosec-measures">Measures to address risk</label>
            <input
              id="infosec-measures"
              type="text"
              value={formData.measures}
              onChange={(event) => setFormData({ ...formData, measures: event.target.value })}
              placeholder="Enter measures to address risk"
            />
          </div>
        </div>
        <ModalActions
          onClose={onClose}
          isSubmitting={isSubmitting}
          submitLabel={submitLabel}
          submittingLabel={submittingLabel}
        />
      </form>
    </ModalShell>
  );
};

InfoSecEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  data: PropTypes.object,
  categoryOptions: ID_LABEL_OPTION_PROP_TYPE,
  impactOptions: ID_LABEL_OPTION_PROP_TYPE,
  riskStatusOptions: ID_LABEL_OPTION_PROP_TYPE,
  likelihoodOptions: ID_LABEL_OPTION_PROP_TYPE,
  mitigationOptions: ID_LABEL_OPTION_PROP_TYPE,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  submittingLabel: PropTypes.string,
};
